import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Plus, Search } from "lucide-react";
import {
  ClinicianPortalApiError,
  clinicianPortalApi,
} from "../api/clinicianPortalApi";
import {
  CopingSkillGuide,
  ClientAccessScope,
  LovOption,
  PortalPatient,
  PortalTherapist,
  UpdateCopingSkillFields,
} from "../api/contracts";
import { sortLovOptions } from "../config/lov";
import { usePortalData } from "../hooks/usePortalData";
import { usePortalMutation } from "../hooks/usePortalMutation";
import {
  ActionsColumnHeader,
  ContentSection,
  PortalDataTable,
  PortalTableColumn,
  StatusPill,
} from "./DataDisplay";
import { ErrorState, LoadingState } from "./FeedbackStates";
import { RowActionMenu } from "./HomeworkControls";

export type CopingSkillScope =
  | { kind: "shared" }
  | { kind: "client"; patientId: string };

type EditorMode =
  | { kind: "create" }
  | { kind: "edit"; copingSkill: CopingSkillGuide };

interface FormErrors {
  copingStrategyOptionId?: string;
  guideSteps?: string;
  form?: string;
}

interface SkillOption {
  id: string;
  name: string;
  enabled: boolean;
  sortOrder: number;
}

function mutationErrors(error: unknown): FormErrors {
  if (!(error instanceof ClinicianPortalApiError)) {
    return { form: "The Coping Skill could not be saved. Please try again." };
  }
  switch (error.code) {
    case "DUPLICATE_COPING_SKILL":
    case "DUPLICATE_CLIENT_COPING_SKILL":
      return {
        copingStrategyOptionId:
          "A guide already exists for this Coping Strategy. Reactivate the archived guide instead.",
      };
    case "INVALID_COPING_STRATEGY_OPTION_ID":
    case "COPING_STRATEGY_OPTION_NOT_FOUND":
    case "COPING_STRATEGY_OPTION_DISABLED":
    case "INVALID_LOV_VALUE":
    case "DISABLED_LOV_VALUE":
      return { copingStrategyOptionId: "Select an available Coping Strategy." };
    case "INVALID_GUIDE_STEPS":
    case "REQUIRED_FIELD_MISSING":
      return { guideSteps: "Guide Steps are required." };
    default:
      return { form: "The Coping Skill could not be saved. Please try again." };
  }
}

function normalizeOptions(options: LovOption[]): SkillOption[] {
  return options.map((option) => ({
    id: option.id,
    name: option.name,
    enabled: option.enabled,
    sortOrder: option.sortOrder,
  }));
}

function CopingSkillEditor({
  clinicianCode,
  scope,
  mode,
  options,
  copingSkills,
  onCancel,
  onSaved,
}: {
  clinicianCode: string;
  scope: CopingSkillScope;
  mode: EditorMode;
  options: SkillOption[];
  copingSkills: CopingSkillGuide[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const mutation = usePortalMutation();
  const submittingRef = useRef(false);
  const current = mode.kind === "edit" ? mode.copingSkill : undefined;
  const usedByOtherGuide = useMemo(
    () =>
      new Set(
        copingSkills
          .filter((skill) => skill.id !== current?.id)
          .map((skill) => skill.copingStrategyOptionId),
      ),
    [copingSkills, current?.id],
  );
  const availableOptions = useMemo(() => {
    const result = options.filter(
      (option) =>
        (option.enabled && !usedByOtherGuide.has(option.id)) ||
        option.id === current?.copingStrategyOptionId,
    );
    if (
      current &&
      !result.some((option) => option.id === current.copingStrategyOptionId)
    ) {
      result.push({
        id: current.copingStrategyOptionId,
        name: current.skillName,
        enabled: false,
        sortOrder: Number.MAX_SAFE_INTEGER,
      });
    }
    return result;
  }, [current, options, usedByOtherGuide]);
  const [copingStrategyOptionId, setCopingStrategyOptionId] = useState(
    current?.copingStrategyOptionId ?? availableOptions[0]?.id ?? "",
  );
  const [guideSteps, setGuideSteps] = useState(current?.guideSteps ?? "");
  const [validationErrors, setValidationErrors] = useState<FormErrors>({});
  const apiErrors =
    mutation.state.status === "error"
      ? mutationErrors(mutation.state.error)
      : {};
  const errors = { ...validationErrors, ...apiErrors };
  const isSubmitting = mutation.state.status === "pending";
  const heading =
    mode.kind === "create" ? "Add Coping Skill" : "Edit Coping Skill";
  const headingId = React.useId();
  const optionInputId = React.useId();
  const stepsInputId = React.useId();
  const optionErrorId = React.useId();
  const stepsErrorId = React.useId();

  const clearErrors = () => {
    setValidationErrors({});
    mutation.reset();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingRef.current || isSubmitting) return;
    const normalizedGuideSteps = guideSteps.trim();
    const nextErrors: FormErrors = {};
    if (!copingStrategyOptionId) {
      nextErrors.copingStrategyOptionId = "Name of Coping Skill is required.";
    }
    if (!normalizedGuideSteps) {
      nextErrors.guideSteps = "Guide Steps are required.";
    }
    setValidationErrors(nextErrors);
    mutation.reset();
    if (Object.keys(nextErrors).length) return;

    if (
      current &&
      copingStrategyOptionId === current.copingStrategyOptionId &&
      normalizedGuideSteps === current.guideSteps
    ) {
      onCancel();
      return;
    }

    submittingRef.current = true;
    try {
      const action =
        mode.kind === "create" ? "create" : `edit:${mode.copingSkill.id}`;
      const result = await mutation.run(action, (signal) => {
        if (mode.kind === "create") {
          const fields = {
            copingStrategyOptionId,
            guideSteps: normalizedGuideSteps,
          };
          return scope.kind === "shared"
            ? clinicianPortalApi.createCopingSkill(
                clinicianCode,
                fields,
                signal,
              )
            : clinicianPortalApi.createClientCopingSkill(
                clinicianCode,
                scope.patientId,
                fields,
                signal,
              );
        }

        const fields: UpdateCopingSkillFields = {};
        if (
          copingStrategyOptionId !== mode.copingSkill.copingStrategyOptionId
        ) {
          fields.copingStrategyOptionId = copingStrategyOptionId;
        }
        if (normalizedGuideSteps !== mode.copingSkill.guideSteps) {
          fields.guideSteps = normalizedGuideSteps;
        }
        return scope.kind === "shared"
          ? clinicianPortalApi.updateCopingSkill(
              clinicianCode,
              mode.copingSkill.id,
              fields,
              signal,
            )
          : clinicianPortalApi.updateClientCopingSkill(
              clinicianCode,
              scope.patientId,
              mode.copingSkill.id,
              fields,
              signal,
            );
      });
      if (result) onSaved();
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <section className="cp-client-value-editor" aria-labelledby={headingId}>
      <div className="cp-client-value-editor__header">
        <h3 id={headingId}>{heading}</h3>
        <p>
          Select an existing Coping Strategy and add clinician-authored
          guidance.
        </p>
      </div>
      <form onSubmit={handleSubmit} noValidate>
        <div className="cp-form-field">
          <label htmlFor={optionInputId}>
            Name of Coping Skill <span aria-hidden="true">*</span>
          </label>
          <select
            id={optionInputId}
            className="cp-form-input"
            value={copingStrategyOptionId}
            required
            disabled={isSubmitting}
            aria-invalid={Boolean(errors.copingStrategyOptionId)}
            aria-describedby={
              errors.copingStrategyOptionId ? optionErrorId : undefined
            }
            onChange={(event) => {
              clearErrors();
              setCopingStrategyOptionId(event.target.value);
            }}
          >
            <option value="" disabled>
              Select a Coping Strategy
            </option>
            {availableOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
                {option.enabled ? "" : " (Unavailable)"}
              </option>
            ))}
          </select>
          {errors.copingStrategyOptionId ? (
            <p className="cp-form-error" id={optionErrorId}>
              {errors.copingStrategyOptionId}
            </p>
          ) : null}
        </div>
        <div className="cp-form-field">
          <label htmlFor={stepsInputId}>
            Guide Steps <span aria-hidden="true">*</span>
          </label>
          <textarea
            id={stepsInputId}
            className="cp-form-input cp-form-textarea cp-coping-skill-editor__steps"
            value={guideSteps}
            required
            disabled={isSubmitting}
            aria-invalid={Boolean(errors.guideSteps)}
            aria-describedby={errors.guideSteps ? stepsErrorId : undefined}
            onChange={(event) => {
              clearErrors();
              setGuideSteps(event.target.value);
            }}
          />
          {errors.guideSteps ? (
            <p className="cp-form-error" id={stepsErrorId}>
              {errors.guideSteps}
            </p>
          ) : null}
        </div>
        {errors.form ? (
          <p className="cp-form-error cp-form-error--panel" role="alert">
            {errors.form}
          </p>
        ) : null}
        <div className="cp-form-actions">
          <button
            type="button"
            className="cp-button cp-button--secondary"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="cp-button cp-button--primary"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? mode.kind === "create"
                ? "Adding…"
                : "Saving…"
              : mode.kind === "create"
                ? "Add Coping Skill"
                : "Save"}
          </button>
        </div>
      </form>
    </section>
  );
}

function ArchiveCopingSkillDialog({
  copingSkill,
  isArchiving,
  hasError,
  onCancel,
  onConfirm,
}: {
  copingSkill: CopingSkillGuide | null;
  isArchiving: boolean;
  hasError: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = React.useId();
  const descriptionId = React.useId();
  useEffect(() => {
    if (copingSkill) cancelButtonRef.current?.focus();
  }, [copingSkill]);
  if (!copingSkill) return null;

  return (
    <div
      className="cp-confirm-dialog__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isArchiving) onCancel();
      }}
    >
      <div
        className="cp-confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onKeyDown={(event) => {
          if (event.key === "Escape" && !isArchiving) {
            event.preventDefault();
            onCancel();
          }
        }}
      >
        <h2 id={titleId}>Archive this Coping Skill?</h2>
        <p id={descriptionId}>
          {copingSkill.skillName} will stop appearing as an enabled guide, but
          it will remain available for historical records.
        </p>
        {hasError ? (
          <p className="cp-confirm-dialog__error" role="alert">
            The Coping Skill could not be archived. Please try again.
          </p>
        ) : null}
        <div className="cp-confirm-dialog__actions">
          <button
            ref={cancelButtonRef}
            type="button"
            className="cp-button cp-button--secondary"
            disabled={isArchiving}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="cp-button cp-button--danger"
            disabled={isArchiving}
            onClick={onConfirm}
          >
            {isArchiving ? "Archiving…" : "Archive Coping Skill"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function CopingSkillsManager({
  clinicianCode,
  scope,
  onTherapistLoaded,
  onClientContextLoaded,
  readOnly = false,
  accessScope = "assigned",
}: {
  clinicianCode: string;
  scope: CopingSkillScope;
  onTherapistLoaded?: (therapist: PortalTherapist) => void;
  onClientContextLoaded?: (
    therapist: PortalTherapist,
    patient: PortalPatient,
  ) => void;
  readOnly?: boolean;
  accessScope?: ClientAccessScope;
}) {
  const isShared = scope.kind === "shared";
  const scopePatientId = scope.kind === "client" ? scope.patientId : undefined;
  const request = useCallback(
    async (signal: AbortSignal) => {
      const [formConfig, skillsData] = await Promise.all([
        clinicianPortalApi.getRecordFormConfig(clinicianCode, signal),
        isShared
          ? accessScope === "team"
            ? clinicianPortalApi.getCopingSkills(clinicianCode, signal, "team")
            : clinicianPortalApi.getCopingSkills(clinicianCode, signal)
          : accessScope === "team"
            ? clinicianPortalApi.getClientCopingSkills(
                clinicianCode,
                scopePatientId as string,
                signal,
                "team",
              )
            : clinicianPortalApi.getClientCopingSkills(
                clinicianCode,
                scopePatientId as string,
                signal,
              ),
      ]);
      return { formConfig, skillsData };
    },
    [accessScope, clinicianCode, isShared, scopePatientId],
  );
  const state = usePortalData(request);
  const mutation = usePortalMutation();
  const resetMutation = mutation.reset;
  const [cachedData, setCachedData] = useState<
    Awaited<ReturnType<typeof request>> | undefined
  >();
  const [editor, setEditor] = useState<EditorMode | null>(null);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<CopingSkillGuide | null>(
    null,
  );
  const latestData =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;

  useEffect(() => {
    if (latestData) setCachedData(latestData);
  }, [latestData]);
  const data = latestData ?? cachedData;
  const therapist = data?.skillsData.therapist;
  useEffect(() => {
    if (therapist && onTherapistLoaded) onTherapistLoaded(therapist);
  }, [onTherapistLoaded, therapist]);
  const clientPatient = data?.skillsData.patient;
  useEffect(() => {
    if (
      scope.kind === "client" &&
      therapist &&
      clientPatient &&
      onClientContextLoaded
    ) {
      onClientContextLoaded(therapist, clientPatient);
    }
  }, [clientPatient, onClientContextLoaded, scope.kind, therapist]);

  const copingField = data?.formConfig.fields.find(
    (field) =>
      field.fieldKey === "copingStrategies" &&
      field.lovCatalogKey === "coping_strategies" &&
      field.catalog?.key === "coping_strategies",
  );
  const lovOptions = useMemo(() => {
    if (!copingField?.catalog) return [];
    return normalizeOptions(
      sortLovOptions(
        copingField.catalog.options,
        copingField.catalog.orderingMode,
      ),
    );
  }, [copingField]);
  const patientId = scopePatientId ?? null;
  const copingSkills = (data?.skillsData.guides ?? []).filter(
    (skill) => skill.patientId === patientId,
  );
  const activeSkills = copingSkills.filter((skill) => skill.enabled);
  const archivedSkills = copingSkills.filter((skill) => !skill.enabled);
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const matchesSearch = (skill: CopingSkillGuide) =>
    !normalizedSearch ||
    [skill.skillName, skill.copingStrategyValue, skill.guideSteps].some(
      (value) => value.toLocaleLowerCase().includes(normalizedSearch),
    );
  const filteredActiveSkills = activeSkills.filter(matchesSearch);
  const filteredArchivedSkills = archivedSkills.filter(matchesSearch);
  const usedOptionIds = new Set(
    copingSkills.map((skill) => skill.copingStrategyOptionId),
  );
  const availableForCreate = lovOptions.filter(
    (option) => option.enabled && !usedOptionIds.has(option.id),
  );
  const isMutating = mutation.state.status === "pending";
  const formAvailable = Boolean(
    data?.formConfig.found &&
      data.formConfig.authorized &&
      data.formConfig.therapist &&
      copingField?.catalog,
  );
  const skillsAvailable = Boolean(
    data?.skillsData.found &&
      data.skillsData.authorized &&
      data.skillsData.therapist &&
      (scope.kind === "shared" ||
        (data.skillsData.patient &&
          data.skillsData.patient.id === scope.patientId)),
  );
  const available = formAvailable && skillsAvailable;
  const initialLoading = !data && state.status === "loading";
  const initialError = !data && state.status === "error";

  const handleSaved = () => {
    setEditor(null);
    setArchiveTarget(null);
    mutation.reset();
    state.refresh();
  };

  const setEnabled = async (
    copingSkill: CopingSkillGuide,
    enabled: boolean,
  ) => {
    if (readOnly || isMutating) return;
    mutation.reset();
    const action = `${enabled ? "reactivate" : "archive"}:${copingSkill.id}`;
    const result = await mutation.run(action, (signal) =>
      scope.kind === "shared"
        ? clinicianPortalApi.setCopingSkillEnabled(
            clinicianCode,
            copingSkill.id,
            enabled,
            signal,
          )
        : clinicianPortalApi.setClientCopingSkillEnabled(
            clinicianCode,
            scope.patientId,
            copingSkill.id,
            enabled,
            signal,
          ),
    );
    if (result) handleSaved();
  };

  const title =
    scope.kind === "shared" ? "Coping Skills" : "Personalized Coping Skills";
  const helper =
    scope.kind === "shared"
      ? "Manage shared clinician guidance for coping strategies."
      : "Create individualized coping skills that are tailored to this client.";
  const empty =
    scope.kind === "shared"
      ? "No coping skills found."
      : "No personalized coping skills found.";
  const addButton = available ? (
    <button
      type="button"
      className="cp-button cp-button--primary"
      disabled={
        isMutating || Boolean(editor) || availableForCreate.length === 0
      }
      onClick={() => {
        mutation.reset();
        setEditor({ kind: "create" });
      }}
    >
      <Plus aria-hidden="true" />
      Add Coping Skill
    </button>
  ) : null;
  const sharedColumns = useMemo<readonly PortalTableColumn<CopingSkillGuide>[]>(
    () => [
      {
        key: "actions",
        header: <ActionsColumnHeader />,
        headerLabel: "Actions",
        initialWidth: 60,
        minWidth: 60,
        resizable: false,
        cellClassName: "cp-table-cell--compact",
        render: (skill) => (
          <RowActionMenu
            clinicianCode={clinicianCode}
            activityName={skill.skillName}
            canEdit={!isMutating && !editor}
            onEdit={() => {
              resetMutation();
              setEditor({ kind: "edit", copingSkill: skill });
            }}
            showDuplicate={false}
            canDelete={!isMutating && !editor}
            onDelete={() => {
              resetMutation();
              setArchiveTarget(skill);
            }}
            deleteLabel="Archive"
            deletingLabel="Archiving…"
          />
        ),
      },
      {
        key: "skillName",
        header: "Coping Skill",
        initialWidth: 280,
        minWidth: 200,
        render: (skill) => (
          <div className="cp-coping-skill-table__name">
            <strong>{skill.skillName}</strong>
            {!skill.copingStrategyEnabled ? (
              <small className="cp-coping-skill-card__unavailable">
                Coping Strategy unavailable — historical guide retained
              </small>
            ) : null}
          </div>
        ),
      },
      {
        key: "status",
        header: "Status",
        initialWidth: 130,
        minWidth: 110,
        resizable: false,
        render: () => <StatusPill status="Enabled" />,
      },
      {
        key: "guideSteps",
        header: "Guide Steps",
        initialWidth: 670,
        minWidth: 320,
        cellClassName: "cp-table-cell--multiline",
        render: (skill) => (
          <div className="cp-activity-steps-preview">{skill.guideSteps}</div>
        ),
      },
    ],
    [clinicianCode, editor, isMutating, resetMutation],
  );
  const personalizedColumns = useMemo<
    readonly PortalTableColumn<CopingSkillGuide>[]
  >(
    () => [
      {
        key: "actions",
        header: <ActionsColumnHeader />,
        headerLabel: "Actions",
        initialWidth: 60,
        minWidth: 60,
        resizable: false,
        cellClassName: "cp-table-cell--compact",
        render: (skill) => (
          <RowActionMenu
            clinicianCode={clinicianCode}
            activityName={`${skill.skillName} Coping Skill`}
            canEdit={!isMutating && !editor}
            onEdit={() => {
              resetMutation();
              setEditor({ kind: "edit", copingSkill: skill });
            }}
            showDuplicate={false}
            canDelete={!isMutating && !editor}
            onDelete={() => {
              resetMutation();
              setArchiveTarget(skill);
            }}
            deleteLabel="Archive"
            deletingLabel="Archiving…"
          />
        ),
      },
      {
        key: "skillName",
        header: "Name of Coping Skill",
        initialWidth: 300,
        minWidth: 220,
        render: (skill) => (
          <div className="cp-coping-skill-table__name">
            <strong>{skill.skillName}</strong>
            {!skill.copingStrategyEnabled ? (
              <small className="cp-coping-skill-card__unavailable">
                Coping Strategy unavailable — historical guide retained
              </small>
            ) : null}
          </div>
        ),
      },
      {
        key: "guidedSteps",
        header: "Guided Steps",
        initialWidth: 820,
        minWidth: 380,
        cellClassName: "cp-table-cell--multiline",
        render: (skill) => (
          <div className="cp-activity-steps-preview">{skill.guideSteps}</div>
        ),
      },
    ],
    [clinicianCode, editor, isMutating, resetMutation],
  );

  return (
    <ContentSection
      title={title}
      hideTitle
      actions={!isShared && !readOnly ? addButton : null}
    >
      {isShared && available ? (
        <>
          <div className="cp-content-header">
            <div>
              <p>{helper}</p>
            </div>
            <strong className="cp-record-count">
              {copingSkills.length} coping skill
              {copingSkills.length === 1 ? "" : "s"}
            </strong>
          </div>
          <div className="cp-list-toolbar">
            <label className="cp-search-control">
              <Search aria-hidden="true" />
              <span className="cp-visually-hidden">Search coping skills</span>
              <input
                type="search"
                value={search}
                placeholder="Search coping skills"
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            {addButton}
          </div>
        </>
      ) : !isShared ? (
        <p className="cp-coping-skills__helper">{helper}</p>
      ) : null}
      {initialLoading ? <LoadingState label={`Loading ${title}`} /> : null}
      {initialError ? (
        <ErrorState error={state.error} onRetry={state.refresh} />
      ) : null}
      {data && !available ? (
        <ErrorState error={new Error(`${title} are not available`)} />
      ) : null}
      {available ? (
        <div className="cp-client-values cp-coping-skills">
          {state.status === "error" && !initialError ? (
            <div
              className="cp-inline-notice cp-inline-notice--error"
              role="alert"
            >
              <div>
                <strong>{title} could not be refreshed.</strong>
                <span>The last loaded guides remain visible.</span>
              </div>
              <button type="button" onClick={state.refresh}>
                Try again
              </button>
            </div>
          ) : null}
          {editor && !readOnly ? (
            <CopingSkillEditor
              key={editor.kind === "edit" ? editor.copingSkill.id : "create"}
              clinicianCode={clinicianCode}
              scope={scope}
              mode={editor}
              options={lovOptions}
              copingSkills={copingSkills}
              onCancel={() => setEditor(null)}
              onSaved={handleSaved}
            />
          ) : null}
          {availableForCreate.length === 0 ? (
            <p className="cp-coping-skills__availability">
              {lovOptions.some((option) => option.enabled)
                ? "All available Coping Strategies already have guides. Reactivate an archived guide instead of creating a duplicate."
                : "No enabled Coping Strategies are available."}
            </p>
          ) : null}
          {filteredActiveSkills.length && isShared ? (
            <PortalDataTable
              columns={sharedColumns}
              records={filteredActiveSkills}
              getRecordKey={(skill) => skill.id}
              caption="Active Coping Skills"
            />
          ) : filteredActiveSkills.length ? (
            <PortalDataTable
              columns={readOnly ? personalizedColumns.slice(1) : personalizedColumns}
              records={filteredActiveSkills}
              getRecordKey={(skill) => skill.id}
              caption="Active Personalized Coping Skills"
            />
          ) : (
            <div className="cp-client-values__empty">
              <p>
                {normalizedSearch
                  ? `No ${title.toLocaleLowerCase()} match your search.`
                  : empty}
              </p>
              {archivedSkills.length ? (
                <small>Archived guides can be displayed below.</small>
              ) : null}
            </div>
          )}
          {archivedSkills.length ? (
            <div className="cp-client-values__archived">
              <button
                type="button"
                className="cp-client-values__archive-toggle"
                aria-expanded={showArchived}
                onClick={() => setShowArchived((current) => !current)}
              >
                {showArchived ? "Hide" : "Show"} archived (
                {archivedSkills.length})
              </button>
              {showArchived ? (
                <div
                  className="cp-client-value-list"
                  aria-label={`Archived ${title}`}
                >
                  {filteredArchivedSkills.map((skill) => {
                    const action = `reactivate:${skill.id}`;
                    const isReactivating =
                      mutation.state.status === "pending" &&
                      mutation.state.action === action;
                    const hasError =
                      mutation.state.status === "error" &&
                      mutation.state.action === action;
                    return (
                      <article
                        className="cp-client-value-card cp-client-value-card--archived cp-coping-skill-card"
                        key={skill.id}
                      >
                        <div className="cp-client-value-card__content">
                          <div className="cp-client-value-card__title">
                            <h3>{skill.skillName}</h3>
                            <StatusPill status="Archived" />
                          </div>
                          <p className="cp-coping-skill-card__steps">
                            {skill.guideSteps}
                          </p>
                          {hasError ? (
                            <p
                              className="cp-client-value-card__error"
                              role="alert"
                            >
                              The Coping Skill could not be reactivated. Please
                              try again.
                            </p>
                          ) : null}
                        </div>
                        <div className="cp-client-value-card__actions">
                          <button
                            type="button"
                            className="cp-button cp-button--secondary"
                            aria-label={`Reactivate ${skill.skillName} Coping Skill`}
                            disabled={isMutating || Boolean(editor)}
                            onClick={() => setEnabled(skill, true)}
                          >
                            {isReactivating ? "Reactivating…" : "Reactivate"}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      <ArchiveCopingSkillDialog
        copingSkill={archiveTarget}
        isArchiving={Boolean(
          archiveTarget &&
            mutation.state.status === "pending" &&
            mutation.state.action === `archive:${archiveTarget.id}`,
        )}
        hasError={Boolean(
          archiveTarget &&
            mutation.state.status === "error" &&
            mutation.state.action === `archive:${archiveTarget.id}`,
        )}
        onCancel={() => {
          setArchiveTarget(null);
          mutation.reset();
        }}
        onConfirm={() => {
          if (archiveTarget) setEnabled(archiveTarget, false);
        }}
      />
    </ContentSection>
  );
}
